import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { api } from '../../lib/api';
import { Loader2, TrendingUp, ArrowUpRight } from 'lucide-react';

const pct = (v) => `${(v ?? 0).toFixed(1)}%`;

const CategoryAnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/reports/analytics/categories');
        if (res.ok) {
          const data = await res.json();
          setCategories(data.categories || []);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const maxHired = Math.max(1, ...categories.map(c => c.hired || 0));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-tp-accent" />
      </div>
    );
  }

  const hasData = categories.some(c => c.total_candidates > 0);

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8" data-testid="category-analytics-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-display font-bold text-tp-text flex items-center gap-2">
          <TrendingUp className="h-6 w-6 md:h-7 md:w-7 text-tp-accent" />
          Catégories qui convertissent
        </h1>
        <p className="text-sm text-tp-text-light mt-1">
          Taux de conversion réel par catégorie — identifiez quels profils embauchent vraiment.
        </p>
      </div>

      {!hasData ? (
        <Card className="p-10 text-center border-dashed">
          <p className="text-sm text-gray-500">
            Pas encore assez de données. Les taux s'afficheront dès les premières décisions clients et embauches.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden" data-testid="category-analytics-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs uppercase text-gray-500">
                  <th className="px-4 py-3 font-semibold">Catégorie</th>
                  <th className="px-4 py-3 font-semibold text-right">Candidats</th>
                  <th className="px-4 py-3 font-semibold text-right">Présentés</th>
                  <th className="px-4 py-3 font-semibold text-right">Intéressés</th>
                  <th className="px-4 py-3 font-semibold text-right">Embauchés</th>
                  <th className="px-4 py-3 font-semibold text-right">% Conv.</th>
                  <th className="px-4 py-3 font-semibold">Répartition</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {categories.map((c) => {
                  const widthPct = c.hired > 0 ? (c.hired / maxHired) * 100 : 0;
                  return (
                    <tr key={c.category_id} className="hover:bg-gray-50/70">
                      <td className="px-4 py-3">
                        <span
                          className="inline-block px-2.5 py-1 rounded-md text-xs font-medium"
                          style={{ backgroundColor: `${c.color}1A`, color: c.color }}
                        >
                          {c.label_fr}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-tp-text font-medium">{c.total_candidates}</td>
                      <td className="px-4 py-3 text-right text-tp-text-light">{c.presented}</td>
                      <td className="px-4 py-3 text-right text-tp-text-light">{c.interested}</td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">{c.hired}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-semibold ${c.overall_conversion >= 20 ? 'text-green-600' : c.overall_conversion >= 10 ? 'text-orange-500' : 'text-gray-400'}`}>
                          {pct(c.overall_conversion)}
                        </span>
                      </td>
                      <td className="px-4 py-3 min-w-[180px]">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${widthPct}%`, backgroundColor: c.color }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="p-4 md:p-6 bg-blue-50/50 border-blue-100">
        <p className="text-sm text-blue-900 flex items-start gap-2">
          <ArrowUpRight className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            <strong>Comment lire :</strong> Une catégorie à fort taux de conversion = priorité de sourcing.
            Une catégorie avec beaucoup de candidats mais peu d'embauches = canal à revoir (qualification, benchmark, attentes client).
          </span>
        </p>
      </Card>
    </div>
  );
};

export default CategoryAnalyticsPage;
